import {
  Header,
  Select,
  Input,
  Button,
  Segment,
  SegmentGroup,
} from "semantic-ui-react";
import SelectOperator from "@shared/SelectOperator";
import React from "react";
import { connect } from "react-redux";
import {
  fetchComplianceMetadata,
  fetchComplianceModel,
  fetchComplianceRecords,
  updateCompliance,
  deleteCompliance,
  createCompliance,
  createReviewQuestion,
  fetchCustomerConfig,
  fetchAlertConfig,
  updateCustomerConfig,
  fetchAgeConfig,
  updateAgeConfig,
  clearComplianceMetadata,
  clearComplianceRecords,
  fetchCustomerInteractionConfig,
  updateCustomerInteractionConfig,
} from "@actions/compliance";
import ComplianceTable from "./ComplianceTable";
import { showSuccessAlert, confirmModal } from "@actions/modals";
import AuthenticationClient from "@common/api/AuthenticationClient";
import { epochInSeconds } from "@common/components/helpers/datetime";
import { handleError } from "@common/components/helpers/error";
import { fetchTenants } from "@api/useradmin.js";
import ReviewConfigurationClient from "@common/api/ReviewConfigurationClient";
import { ageConfigLabels } from "@reducers/complianceReducer";
import { mapToSelect } from "@common/components/helpers/forms";
import SelectFieldType from "../Compliance/filters/SelectFieldType";
import ToggleFieldType from "../Compliance/filters/ToggleFieldType";
import {
  fetchComplianceSummaryMetadata,
  updateAlertConfig,
} from "../../redux/actions/compliance";
import { useEffect } from "react";
import { useState } from "react";
import AlertModalAction from "@common/redux/actions/AlertModalAction";

// Simple function to get keys starting with 'has'
const getHasKeys = (obj) => {
  return Object.keys(obj).filter((key) => key.startsWith("has"));
};

const mapLabelNames = (label) => {
  if (label === "Event Type") {
    return "Event Configuration";
  }
  return label
    .replace(/Type$/i, "") // Remove "Type" if it appears at the end
    .replace(/([a-z])([A-Z])/g, "$1 $2") // Add space between camel case words
    .trim();
};

class Compliance extends React.Component {
  state = {
    activeIndex: 0,
    selectedDataField: null,
    selectedEventType: null,
    selectedReviewQuestionType: 2, // 1 = Basic Question, 2 = Escalated Question
    loading: false,
    canAdd: true,
    canDelete: true,
    dataFields: [],
    selectedOperator: null,
    tenants: [],
    name: null,
  };

  reviewQuestionTypeOptions = [
    { key: 1, value: 1, text: "Basic Question" },
    { key: 2, value: 2, text: "Escalated Question" },
  ];

  handleOperatorSelect = async (_, { value }) => {
    this.setState({ selectedOperator: value });

    await AuthenticationClient.SwitchTenant(value).then(
      ({ accessToken, refreshToken, expiresIn }) => {
        sessionStorage.setItem("accessToken", accessToken);
        sessionStorage.setItem("refreshToken", refreshToken);
        sessionStorage.setItem("tokenExpires", epochInSeconds() + expiresIn);
        this.resetMetaData();
        this.props.fetchCustomerConfig();
        this.props.fetchAlertConfig();
        this.props.fetchAgeConfig();
        this.props.fetchCustomerInteractionConfig();
        this.props.fetchComplianceSummaryMetadata();
      },
      (error) => handleError(error),
    );
  };

  resetMetaData = () => {
    this.setState({ selectedDataField: null, selectedEventType: null });
    this.props.clearComplianceMetadata();
    this.props.clearComplianceRecords();
    this.props.fetchComplianceMetadata();
  };

  onSelectEventType = async (_e, { value }) => {
    if (value && this.props.metadata) {
      const selectedEventType = this.props.complianceEvents.find(
        (data) => data.value === value,
      );
      const datafields = this.props.metadata.filter(
        (data) => data.parent === value || data.value === value,
      );
      if (datafields.length === 0) {
        datafields.push({
          key: value,
          type: value,
          text: value,
          canAdd: selectedEventType.canAdd,
          canDelete: selectedEventType.canDelete,
          isReadOnly: selectedEventType.isReadOnly,
        });
      }

      const isReviewQuestion = datafields[0].value === "ReviewQuestion";
      this.setState({
        selectedEventType: value,
        dataFields: datafields,
        selectedDataField: datafields[0].type,
        loading: true,
        canAdd: datafields[0].canAdd,
        canDelete: datafields[0].canDelete,
        name: datafields[0].text,
      });
      if (datafields[0].canAdd) {
        try {
          this.props.clearComplianceRecords();
          await this.props.fetchComplianceModel(datafields[0].value);
        } catch (e) {
          console.error(e);
        }
      }
      this.setState({ loading: false });
      await this.props.fetchComplianceRecords(
        datafields[0].value,
        isReviewQuestion ? { reviewTypeId: this.state.selectedReviewQuestionType } : {},
      );
    }
  };

  onSelectDataField = async (_e, { value }) => {
    const selectedEntry = this.state.dataFields.find(
      (data) => data.value === value,
    );
    if (selectedEntry) {
      const isReviewQuestion = value === "ReviewQuestion";
      this.setState({
        selectedDataField: value,
        loading: true,
        canAdd: selectedEntry.canAdd,
        canDelete: selectedEntry.canDelete,
        name: selectedEntry.text,
      });
      this.props.clearComplianceRecords();
      if (selectedEntry.canAdd && selectedEntry.isDisabled) {
        await this.props.fetchComplianceModel(selectedEntry.value);
      }
      this.setState({ loading: false });
      await this.props.fetchComplianceRecords(
        selectedEntry.value,
        isReviewQuestion ? { reviewTypeId: this.state.selectedReviewQuestionType } : {},
      );
    }
  };

  onSelectReviewQuestionType = async (_e, { value }) => {
    this.setState({ selectedReviewQuestionType: value, loading: true });
    this.props.clearComplianceRecords();
    this.setState({ loading: false });
    await this.props.fetchComplianceRecords("ReviewQuestion", {
      reviewTypeId: value,
    });
  };

  async componentDidMount() {
    this.props.fetchComplianceMetadata();
    this.props.fetchCustomerConfig();
    this.props.fetchAlertConfig();
    this.props.fetchAgeConfig();
    this.props.fetchCustomerInteractionConfig();
    this.props.fetchComplianceSummaryMetadata();


    fetchTenants().then((tenants) =>
      this.setState({ tenants: mapToSelect(tenants) }, () => {
        if (this.state.tenants && this.state.tenants.length === 1) {
          this.setState({ selectedOperator: this.state.tenants[0].value });
          this.resetMetaData();
        }
      }),
    );
  }

  onSave = async (data) => {
    const isNewReviewQuestion =
      this.state.selectedDataField === "ReviewQuestion" && !data.id;
    const promise = isNewReviewQuestion
      ? createReviewQuestion(data)
      : !data.id
        ? createCompliance(this.state.selectedDataField, data)
        : updateCompliance(this.state.selectedDataField, data);
    promise.then(
      () => {
        const isReviewQuestion =
          this.state.selectedDataField === "ReviewQuestion";
        this.props.fetchComplianceRecords(
          this.state.selectedDataField,
          isReviewQuestion
            ? { reviewTypeId: this.state.selectedReviewQuestionType }
            : {},
        );
        AlertModalAction.open("The option was saved successfully!", "Success");
      },
      (error) => handleError(error),
    );
  };

  onDelete = async (id) => {
    const callback = async () => {
      await deleteCompliance(this.state.selectedDataField, id);
      await this.props.fetchComplianceRecords(this.state.selectedDataField);
    };

    this.props.confirmModal(
      "Are you sure you want to delete the entry?",
      true,
      callback,
      () => {
        this.props.confirmModal("", false);
      },
      true,
    );
  };

  handleToggleChange = ({ fnUpdate, currentValues, name, value }) => {
    const payload = { ...currentValues, [name]: value };
    fnUpdate(payload).then(() =>
      this.props.showSuccessAlert("Field type successfully updated!"),
    );
  };

  render() {
    const { complianceTabSelectTenantText } = this.props.login;

    const isTypeEditable =
      this.state.selectedOperator &&
      this.state.selectedEventType === "Customer Profile" &&
      Object.keys(this.props.customerConfig).includes(
        `has${this.state.selectedDataField}`,
      );

    const alertConfig = this.props.alertConfig;
    const {
      filters: { eventTypes, incidentTypes },
    } = this.props.complianceSummaryTab;

    const showBundledEventType =
      this.state.selectedOperator && this.state.selectedEventType === "Alerts";

    const isReviewDataField = this.state.selectedDataField === "ReviewToggle";

    const isTemporaryBreakField = this.state.selectedDataField === "SetTemporaryBreak";

    const isVulnerableField = this.state.selectedDataField === "SetVulnerable";

    const showBundledIncidentType =
      showBundledEventType && alertConfig?.defaultBundledEventTypeId === 3;

    const isAgeVerificationOptionalFields =
      this.state.selectedDataField === "OptionalFields";

    const { fieldRequiredTypes } = this.props.customerConfig;

    return (
      <div className="p-1">
        <Header size="small">Compliance Configuration</Header>

        <SelectOperator
          placeholder={complianceTabSelectTenantText}
          operators={this.state.tenants}
          value={this.state.selectedOperator}
          onChange={this.handleOperatorSelect}
          className="mr-1"
        />

        <Select
          placeholder="Select Type"
          disabled={!this.state.selectedOperator}
          options={this.props.complianceEvents}
          value={this.state.selectedEventType}
          onChange={this.onSelectEventType}
          className="mr-1"
        />

        <Select
          placeholder="Select Item"
          options={this.state.dataFields}
          disabled={!this.state.selectedOperator}
          value={this.state.selectedDataField}
          onChange={this.onSelectDataField}
          className="mr-1"
        />

        {this.state.selectedDataField === "ReviewQuestion" && (
          <Select
            placeholder="Select question type"
            options={this.reviewQuestionTypeOptions}
            value={this.state.selectedReviewQuestionType}
            onChange={this.onSelectReviewQuestionType}
            className="mr-1"
          />
        )}

        {isTypeEditable && (
          <SelectFieldType
            selectedDataField={this.state.selectedDataField}
            name={`has${this.state.selectedDataField}`}
            value={
              this.props.customerConfig[`has${this.state.selectedDataField}`]
            }
            onChange={(_e, { name, value }) =>
              this.handleToggleChange({
                fnUpdate: this.props.updateCustomerConfig,
                currentValues: this.props.customerConfig,
                name,
                value,
              })
            }
            options={mapToSelect(fieldRequiredTypes)}
          />
        )}

        {showBundledEventType && (
          <SelectFieldType
            selectedDataField={alertConfig?.defaultBundledEventTypeId}
            name="defaultBundledEventTypeId"
            value={alertConfig?.defaultBundledEventTypeId}
            onChange={(_e, { name, value }) =>
              this.handleToggleChange({
                fnUpdate: this.props.updateAlertConfig,
                currentValues: this.props.alertConfig,
                name,
                value,
              })
            }
            label="Choose Bundled Event Type:"
            options={mapToSelect(eventTypes)}
          />
        )}

        {showBundledIncidentType && (
          <SelectFieldType
            selectedDataField={alertConfig?.defaultBundledIncidentTypeId}
            name="defaultBundledIncidentTypeId"
            value={alertConfig?.defaultBundledIncidentTypeId}
            onChange={(_e, { name, value }) =>
              this.handleToggleChange({
                fnUpdate: this.props.updateAlertConfig,
                currentValues: this.props.alertConfig,
                name,
                value,
              })
            }
            label="Choose Bundled Incident Type:"
            options={mapToSelect(incidentTypes)}
          />
        )}

        {isAgeVerificationOptionalFields && (
          <div className="my-3">
            {getHasKeys(this.props.ageConfig).map(
              (x) =>
                ageConfigLabels[x] && (
                  <ToggleFieldType
                    value={this.props.ageConfig[x]}
                    label={ageConfigLabels[x]}
                    onToggle={(value) => {
                      this.handleToggleChange({
                        fnUpdate: this.props.updateAgeConfig,
                        currentValues: this.props.ageConfig,
                        name: x,
                        value,
                      });
                    }}
                  />
                ),
            )}
          </div>
        )}

        {isTemporaryBreakField && (
          <TemporaryBreakConfig
            customerInteractionConfig={this.props.customerInteractionConfig}
            updateCustomerInteractionConfig={this.props.updateCustomerInteractionConfig}
            showSuccessAlert={this.props.showSuccessAlert}
          />
        )}
        {isVulnerableField && (
          <VulnerableConfig
            customerInteractionConfig={this.props.customerInteractionConfig}
            updateCustomerInteractionConfig={this.props.updateCustomerInteractionConfig}
            showSuccessAlert={this.props.showSuccessAlert}
          />
        )}

        {isReviewDataField && <ReviewTriggerConfig />}

        {this.props.complianceRecords &&
          this.props.complianceRecords.length > 0 && (
            <ComplianceTable
              eventTypes={eventTypes}
              incidentTypes={incidentTypes}
              data={this.props.complianceRecords}
              model={this.props.complianceModel}
              loading={this.state.loading}
              type={this.state.selectedDataField}
              reviewTypeId={this.state.selectedReviewQuestionType}
              onSave={this.onSave}
              onDelete={this.onDelete}
              canAdd={this.state.canAdd}
              canDelete={this.state.canDelete}
              name={this.state.name}
            />
          )}
      </div>
    );
  }
}

const VALUE_YES = 2;
const VALUE_NO = 0;

const TemporaryBreakConfig = ({
  customerInteractionConfig = {},
  updateCustomerInteractionConfig,
  showSuccessAlert,
}) => {
  const handleToggle = (setTemporaryBreak, setVulnerable) => {
    if (customerInteractionConfig.hasSetTemporaryBreak === setTemporaryBreak && customerInteractionConfig.hasSetVulnerable === setVulnerable) return;
    updateCustomerInteractionConfig(setTemporaryBreak, setVulnerable).then(
      () => showSuccessAlert("Field type successfully updated!"),
    ).catch(() => { /* error already shown by handleError */ });
  };

  return (
    <SegmentGroup piled className="my-3">
      <Segment>
        <div className="flex-between flex-row-center my-1">
          <span>Set up a temporary break for the customer</span>
          <Button.Group>
            <Button
              color={customerInteractionConfig.hasSetTemporaryBreak === VALUE_YES ? "green" : null}
              content="Yes"
              onClick={() => handleToggle(VALUE_YES, customerInteractionConfig.hasSetVulnerable)}
            />
            <Button
              color={customerInteractionConfig.hasSetTemporaryBreak === VALUE_NO ? "red" : null}
              content="No"
              onClick={() => handleToggle(VALUE_NO, customerInteractionConfig.hasSetVulnerable)}
            />
          </Button.Group>
        </div>
      </Segment>
    </SegmentGroup>
  );
};

const VulnerableConfig = ({
  customerInteractionConfig = {},
  updateCustomerInteractionConfig,
  showSuccessAlert,  
}) => {
  const handleToggle = (setTemporaryBreak, setVulnerable) => {
    if (customerInteractionConfig.hasSetTemporaryBreak === setTemporaryBreak && customerInteractionConfig.hasSetVulnerable === setVulnerable) return;
    updateCustomerInteractionConfig(setTemporaryBreak, setVulnerable).then(
      () => showSuccessAlert("Field type successfully updated!"),
    ).catch(() => { /* error already shown by handleError */ });
  };

  return (
    <SegmentGroup piled className="my-3">
      <Segment>
        <div className="flex-between flex-row-center my-1">
          <span>Mark this customer as vulnerable?</span>
          <Button.Group>
            <Button
              color={customerInteractionConfig.hasSetVulnerable === VALUE_YES ? "green" : null}
              content="Yes"
              onClick={() => handleToggle(customerInteractionConfig.hasSetTemporaryBreak, VALUE_YES)}
            />
            <Button
              color={customerInteractionConfig.hasSetVulnerable === VALUE_NO ? "red" : null}
              content="No"
              onClick={() => handleToggle(customerInteractionConfig.hasSetTemporaryBreak, VALUE_NO)}
            />
          </Button.Group>
        </div>
      </Segment>
    </SegmentGroup>
  );
};


const ReviewTriggerConfig = () => {
  const [state, setState] = useState({
    record: {},
    triggerOperators: [],
    triggerQuestions: [],
  });
  const { record, triggerOperators, triggerQuestions } = state;
  const onChange = (key, value) =>
    setState({ ...state, record: { ...state.record, [key]: value } });

  useEffect(() => {
    ReviewConfigurationClient.ReadForUpdate().then(
      (response) => setState(response),
      handleError,
    );
  }, []);

  const onSaveClick = () => {
    ReviewConfigurationClient.Update(record).then((record) => {
      setState({ ...state, record });
      AlertModalAction.open("Settings successfully updated!", "Success");
    }, handleError);
  };

  return (
    <SegmentGroup piled>
      <Segment>
        Use
        <Select
          options={mapToSelect(triggerQuestions)}
          value={record?.reviewTriggerQuestionId}
          className="ml-0_5 mr-0_5"
          name="reviewTriggerQuestionId"
          onChange={(_, { name, value }) => onChange(name, value)}
        />
        dropdown to trigger reviews.
      </Segment>
      <Segment>
        Trigger Basic review if risk score is
        <Select
          options={mapToSelect(triggerOperators)}
          className="ml-0_5 mr-0_5"
          name="reviewTriggerOperatorId"
          value={record?.reviewTriggerOperatorId}
          onChange={(_, { name, value }) => onChange(name, value)}
        />
        <Input
          type="number"
          defaultValue={999}
          step="1"
          compact
          style={{ width: "60px" }}
          name="reviewTriggerThreshold"
          value={record?.reviewTriggerThreshold}
          onChange={(_, { name, value }) => onChange(name, value)}
        />
      </Segment>
      <Segment>
        Schedule a Basic review for
        <Input
          type="number"
          step="1"
          compact
          style={{ width: "60px" }}
          className="ml-0_5 mr-0_5"
          value={record?.reviewTriggerDays}
          name="reviewTriggerDays"
          onChange={(_, { name, value }) => onChange(name, value)}
        />{" "}
        days after being triggered by the risk score.
      </Segment>
      <Segment>
        Trigger Escalated review if risk score is
        <Select
          options={mapToSelect(triggerOperators)}
          className="ml-0_5 mr-0_5"
          name="escalatedReviewTriggerOperatorId"
          value={record?.escalatedReviewTriggerOperatorId}
          onChange={(_, { name, value }) => onChange(name, value)}
        />
        <Input
          type="number"
          defaultValue={999}
          step="1"
          compact
          style={{ width: "60px" }}
          name="escalatedReviewTriggerThreshold"
          value={record?.escalatedReviewTriggerThreshold}
          onChange={(_, { name, value }) => onChange(name, value)}
        />
      </Segment>
      <Segment>
        Schedule an Escalated review for
        <Input
          type="number"
          step="1"
          compact
          style={{ width: "60px" }}
          className="ml-0_5 mr-0_5"
          value={record?.escalatedReviewTriggerDays}
          name="escalatedReviewTriggerDays"
          onChange={(_, { name, value }) => onChange(name, value)}
        />{" "}
        days after being triggered by the risk score.
      </Segment>
      <Segment>
        If flagged as vulnerable during interaction trigger review?
        <Button.Group className="ml-1">
          <Button
            color={record?.reviewTriggerVulnerable === true ? "green" : null}
            content="Yes"
            onClick={() => onChange("reviewTriggerVulnerable", true)}
          />
          <Button
            color={record?.reviewTriggerVulnerable === false ? "red" : null}
            content="No"
            onClick={() => onChange("reviewTriggerVulnerable", false)}
          />
        </Button.Group>
      </Segment>
      <Segment>
        If customer is set on temporary break during interaction trigger review?
        <Button.Group className="ml-1">
          <Button
            color={
              record?.reviewTriggerTemporaryBreak === true ? "green" : null
            }
            content="Yes"
            onClick={() => onChange("reviewTriggerTemporaryBreak", true)}
          />
          <Button
            color={record?.reviewTriggerTemporaryBreak === false ? "red" : null}
            onClick={() => onChange("reviewTriggerTemporaryBreak", false)}
            content="No"
          />
        </Button.Group>
      </Segment>
      <Segment>
        <Button positive content="Save" onClick={onSaveClick} />
      </Segment>
    </SegmentGroup>
  );
};

const mapStateToProps = (state) => {
  return {
    metadata: state.complianceMetadata.map((data) => ({
      key: data.type,
      value: data.type,
      text: mapLabelNames(data.name),
      ...data,
    })),
    login: state.logIn,
    complianceModel: state.complianceModel,
    customerConfig: state.complianceCustomerConfig,
    customerInteractionConfig: state.complianceCustomerInteractionConfig,
    alertConfig: state.complianceAlertConfig,
    ageConfig: state.complianceAgeConfig,
    activeComplianceModule: state.activeComplianceModule,
    complianceRecords: state.complianceRecords.data,
    complianceEvents: state.complianceMetadata
      ?.filter((data) => data.parent === "Event")
      .map((data) => ({ value: data.type, text: data.name, ...data }))
      .concat([
        ...state.complianceMetadata
          ?.filter(
            (data) => data.parent !== "Event" && data.parent !== "Compliance",
          )
          .map((data) => ({ value: data.parent, text: data.parent }))
          ?.filter(
            (item, index, arr) =>
              arr.findIndex((_item) => _item.value === item.value) === index,
          ),
      ])
      .map((data) => ({ ...data, text: mapLabelNames(data.text) })),
    complianceSummaryTab: state.complianceSummaryTab,
  };
};

export default connect(mapStateToProps, {
  fetchComplianceMetadata,
  fetchComplianceModel,
  fetchComplianceRecords,
  fetchCustomerConfig,
  fetchAlertConfig,
  fetchAgeConfig,
  fetchComplianceSummaryMetadata,
  confirmModal,
  updateCustomerConfig,
  updateAlertConfig,
  updateAgeConfig,
  clearComplianceMetadata,
  showSuccessAlert,
  clearComplianceRecords,
  fetchCustomerInteractionConfig,
  updateCustomerInteractionConfig,
})(Compliance);
